import React, { useCallback, useEffect, useRef, useState, lazy, Suspense } from "react";
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    LinearProgress,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import StopIcon from "@mui/icons-material/Stop";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import ConversationSidebar from "./components/ConversationSidebar";
import FeedbackButtons from "./components/FeedbackButtons";
import ReportOutput from "./components/ReportOutput";
import SavedReports from "./components/SavedReports";
import {
    createConversation,
    deleteConversation,
    getConversation,
    getConversations,
    getLlmUsageHistory,
    getLlmUsageMonthSummary,
    getReports,
    saveReport,
    sendMessage,
} from "./ai-reports-service";

const MarkdownRenderer = lazy(() =>
    Promise.all([import("react-markdown"), import("remark-gfm")]).then(([mod, gfm]) => ({
        default: ({ children }) =>
            React.createElement(mod.default, { remarkPlugins: [gfm.default] }, children),
    }))
);

// ─── Styled components ────────────────────────────────────────────────────────

const PageContainer = styled(Box)`
    display: flex;
    height: calc(100vh - 68px);
    overflow: hidden;
`;

const MainArea = styled(Box)`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

const TabBar = styled(Box)`
    border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
    padding: ${({ theme }) => theme.spacing(0, 2)};
    flex-shrink: 0;
`;

const MessagesArea = styled(Box)`
    flex: 1;
    overflow-y: auto;
    padding: ${({ theme }) => theme.spacing(3)};
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(2)};
`;

const MessageBubble = styled(Box)`
    max-width: 820px;
    width: 100%;
    align-self: ${({ $align }) => ($align === "user" ? "flex-end" : "flex-start")};
`;

const UserBubble = styled(Box)`
    background-color: ${({ theme }) => theme.palette.primary.main};
    color: ${({ theme }) => theme.palette.primary.contrastText};
    border-radius: 16px 16px 4px 16px;
    padding: ${({ theme }) => theme.spacing(1.5, 2)};
    display: inline-block;
    max-width: 100%;
`;

const AssistantBubble = styled(Box)`
    background-color: ${({ theme }) => theme.palette.background.paper};
    border: 1px solid ${({ theme }) => theme.palette.divider};
    border-radius: 4px 16px 16px 16px;
    padding: ${({ theme }) => theme.spacing(2)};
`;

const InputArea = styled(Box)`
    padding: ${({ theme }) => theme.spacing(2, 3)};
    border-top: 1px solid ${({ theme }) => theme.palette.divider};
    flex-shrink: 0;
`;

const StatusChip = styled(Box)`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background-color: ${({ theme }) => theme.palette.action.hover};
    border-radius: 12px;
    padding: ${({ theme }) => theme.spacing(0.5, 1.5)};
    font-size: 12px;
    color: ${({ theme }) => theme.palette.text.secondary};
`;

const EmptyState = styled(Box)`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${({ theme }) => theme.spacing(2)};
    color: ${({ theme }) => theme.palette.text.secondary};
`;

// ─── Status indicator ─────────────────────────────────────────────────────────

const StreamStatus = ({ status }) => {
    if (!status) return null;
    return (
        <MessageBubble $align="assistant">
            <AssistantBubble>
                <StatusChip>
                    <CircularProgress size={12} />
                    {status}
                </StatusChip>
            </AssistantBubble>
        </MessageBubble>
    );
};

const usageBarColor = (pct) => {
    if (pct == null) return "primary";
    if (pct >= 90) return "error";
    if (pct >= 80) return "warning";
    return "success";
};

/** First instant of next billing window (UTC month); shown in the viewer's local timezone. */
function formatAiUsageBudgetReset(monthEndsAtExclusiveIso) {
    if (!monthEndsAtExclusiveIso) return "";
    const d = new Date(monthEndsAtExclusiveIso);
    if (Number.isNaN(d.getTime())) return "";
    let timeZone;
    try {
        timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
        timeZone = "UTC";
    }
    const opts = { timeZone };
    const datePart = new Intl.DateTimeFormat(undefined, {
        ...opts,
        weekday: "short",
        month: "short",
        day: "numeric",
    }).format(d);
    const timePart = new Intl.DateTimeFormat(undefined, {
        ...opts,
        hour: "numeric",
        minute: "2-digit",
    }).format(d);
    return `(Resets ${datePart} at ${timePart})`;
}

/** Month-to-date AI usage when org has Dynamo caps (GET /api/ai/llm-usage-month). */
const LlmUsageMeter = ({ summary }) => {
    if (!summary?.capsConfigured) return null;
    const pct = summary.tokenUtilizationPercent ?? summary.costUtilizationPercent;
    return (
        <Box
            sx={{
                px: 3,
                py: 1.5,
                borderBottom: 1,
                borderColor: "divider",
                bgcolor: "action.hover",
            }}
        >
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Month-to-date AI usage{" "}
                {formatAiUsageBudgetReset(summary.monthEndsAtExclusiveIso) ||
                    "— counters reset at the start of each calendar month (UTC)."}
                {summary.nearMonthlyLimit ? " — approaching monthly cap" : ""}
            </Typography>
            <Box>
                <Typography variant="caption" color="text.secondary">
                    Tokens: {summary.tokensUsed.toLocaleString()}
                    {summary.tokensLimit != null
                        ? ` / ${summary.tokensLimit.toLocaleString()}`
                        : ""}
                    {pct != null ? ` (${pct}%)` : ""}
                </Typography>
                {pct != null && (
                    <LinearProgress
                        variant="determinate"
                        value={Math.min(100, pct)}
                        color={usageBarColor(pct)}
                        sx={{ mt: 0.5, height: 6, borderRadius: 1 }}
                    />
                )}
            </Box>
        </Box>
    );
};

// ─── Save report dialog ───────────────────────────────────────────────────────

const SaveReportDialog = ({ open, onClose, onSave }) => {
    const [title, setTitle] = useState("");
    const handleSave = () => {
        if (title.trim()) {
            onSave(title.trim());
            setTitle("");
        }
    };
    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Save Report</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    fullWidth
                    size="small"
                    label="Report title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSave()}
                    sx={{ mt: 1 }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={handleSave} disabled={!title.trim()}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ─── Main scene ───────────────────────────────────────────────────────────────

const AiReports = () => {
    const [conversations, setConversations] = useState([]);
    const [activeConversationId, setActiveConversationId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [reports, setReports] = useState([]);
    const [tab, setTab] = useState(0);
    const [inputText, setInputText] = useState("");
    const [streamStatus, setStreamStatus] = useState(null);
    const [streamingDraft, setStreamingDraft] = useState("");
    const [streaming, setStreaming] = useState(false);
    const [error, setError] = useState(null);
    const [saveDialogOpen, setSaveDialogOpen] = useState(false);
    const [messageToSave, setMessageToSave] = useState(null);
    const [convLoading, setConvLoading] = useState(true);
    const [llmUsageSummary, setLlmUsageSummary] = useState(null);
    const [streamErrorCode, setStreamErrorCode] = useState(null);
    const [streamErrorDimension, setStreamErrorDimension] = useState(null);
    const [usageDigestNotice, setUsageDigestNotice] = useState(null);
    const [usageHistory, setUsageHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    const messagesEndRef = useRef(null);
    const abortRef = useRef(null);

    const refreshLlmUsage = useCallback(async () => {
        try {
            const s = await getLlmUsageMonthSummary();
            setLlmUsageSummary(s);
        } catch {
            setLlmUsageSummary(null);
        }
    }, []);

    const loadConversation = useCallback(
        async (id) => {
            setActiveConversationId(id);
            setMessages([]);
            try {
                const conv = await getConversation(id);
                setMessages(conv.messages);
                refreshLlmUsage();
            } catch {
                setError("Failed to load conversation");
            }
        },
        [refreshLlmUsage]
    );

    // ─── Load conversations + reports on mount ────────────────────────────────

    useEffect(() => {
        const load = async () => {
            setConvLoading(true);
            try {
                const [convs, reps] = await Promise.all([getConversations(), getReports()]);
                setConversations(convs);
                setReports(reps);
                await refreshLlmUsage();
                const reportParam = new URLSearchParams(window.location.search).get("report");
                if (reportParam) {
                    setTab(1);
                } else if (convs.length > 0) {
                    await loadConversation(convs[0].id);
                }
            } catch (e) {
                setError("Failed to load data");
            } finally {
                setConvLoading(false);
            }
        };
        load();
    }, [loadConversation, refreshLlmUsage]);

    // ─── Auto-scroll ──────────────────────────────────────────────────────────

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, streamStatus, streamingDraft]);

    // ─── Load usage history when tab 2 is opened ─────────────────────────────

    useEffect(() => {
        if (tab !== 2) return;
        setHistoryLoading(true);
        getLlmUsageHistory()
            .then(setUsageHistory)
            .catch(() => setUsageHistory([]))
            .finally(() => setHistoryLoading(false));
    }, [tab]);

    // ─── New conversation ─────────────────────────────────────────────────────

    const handleNewConversation = useCallback(async () => {
        try {
            const conv = await createConversation();
            setConversations((prev) => [conv, ...prev]);
            setActiveConversationId(conv.id);
            setMessages([]);
        } catch {
            setError("Failed to create conversation");
        }
    }, []);

    // ─── Delete conversation ──────────────────────────────────────────────────

    const handleDeleteConversation = useCallback(
        async (id) => {
            await deleteConversation(id);
            setConversations((prev) => prev.filter((c) => c.id !== id));
            if (activeConversationId === id) {
                setActiveConversationId(null);
                setMessages([]);
            }
        },
        [activeConversationId]
    );

    // ─── Send message ─────────────────────────────────────────────────────────

    const handleSend = useCallback(async () => {
        const text = inputText.trim();
        if (!text || streaming) return;

        let convId = activeConversationId;
        if (!convId) {
            try {
                const conv = await createConversation();
                setConversations((prev) => [conv, ...prev]);
                setActiveConversationId(conv.id);
                convId = conv.id;
            } catch {
                setError("Failed to start conversation");
                return;
            }
        }

        setInputText("");
        setStreaming(true);
        setStreamingDraft("");
        setStreamStatus("Thinking…");
        setError(null);
        setStreamErrorCode(null);
        setStreamErrorDimension(null);
        setUsageDigestNotice(null);

        const tempUserId = `temp-${Date.now()}`;
        // Optimistically add user message
        const tempUserMsg = {
            id: tempUserId,
            role: "user",
            textContent: text,
            structuredOutput: null,
            createdAt: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, tempUserMsg]);

        const controller = new AbortController();
        abortRef.current = controller;

        try {
            await sendMessage(
                convId,
                text,
                (event) => {
                    if (event.type === "thinking")
                        setStreamStatus(event.description || "Thinking…");
                    else if (event.type === "running_query")
                        setStreamStatus(`Running query: ${event.description || ""}…`);
                    else if (event.type === "query_done")
                        setStreamStatus(`Query done (${event.rowCount} rows)`);
                    else if (event.type === "assistant_delta" && event.text) {
                        setStreamingDraft((prev) => prev + event.text);
                        setStreamStatus(null);
                    } else if (event.type === "complete") {
                        setStreamStatus(null);
                        setStreamingDraft("");
                        setStreaming(false);
                        if (event.message) {
                            setMessages((prev) => [...prev, event.message]);
                        }
                        if (event.usageDigest?.nearMonthlyLimit) {
                            const reset =
                                formatAiUsageBudgetReset(
                                    event.usageDigest.monthEndsAtExclusiveIso
                                ) || "(resets at the start of the next calendar month, UTC)";
                            setUsageDigestNotice(
                                `You're at or above 80% of a monthly AI limit ${reset}. Further messages may be blocked at the cap. Contact support@adadot.com if you need a higher limit.`
                            );
                        } else {
                            setUsageDigestNotice(null);
                        }
                        getConversations()
                            .then(setConversations)
                            .catch(() => {});
                        refreshLlmUsage();
                    } else if (event.type === "error") {
                        setMessages((prev) => prev.filter((m) => m.id !== tempUserId));
                        setStreamStatus(null);
                        setStreamingDraft("");
                        setStreaming(false);
                        setStreamErrorCode(event.errorCode || null);
                        setStreamErrorDimension(event.errorDimension || null);
                        setError(event.error || "An error occurred");
                    }
                },
                controller.signal
            );
        } catch (e) {
            setMessages((prev) => prev.filter((m) => m.id !== tempUserId));
            if (e.name !== "AbortError") {
                setError(e.message || "Failed to send message");
            }
            setStreamStatus(null);
            setStreamingDraft("");
            setStreaming(false);
        }
    }, [inputText, streaming, activeConversationId, refreshLlmUsage]);

    // ─── Save report ──────────────────────────────────────────────────────────

    const handleSaveReport = useCallback(
        async (title) => {
            if (!messageToSave) return;
            try {
                const report = await saveReport(messageToSave.id, title, activeConversationId);
                setReports((prev) => [report, ...prev]);
                setSaveDialogOpen(false);
                setMessageToSave(null);
                setTab(1);
            } catch {
                setError("Failed to save report");
            }
        },
        [messageToSave, activeConversationId]
    );

    // ─── Render ───────────────────────────────────────────────────────────────

    return (
        <>
            <Helmet>
                <title>AI Insights Lab – Adadot</title>
            </Helmet>

            <PageContainer>
                <ConversationSidebar
                    conversations={conversations}
                    activeConversationId={activeConversationId}
                    loading={convLoading}
                    onNewConversation={handleNewConversation}
                    onSelectConversation={loadConversation}
                    onDeleteConversation={handleDeleteConversation}
                />

                <MainArea>
                    <TabBar>
                        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ minHeight: 44 }}>
                            <Tab label="Chat" sx={{ minHeight: 44 }} />
                            <Tab label={`Insights (${reports.length})`} sx={{ minHeight: 44 }} />
                            <Tab label="Usage" sx={{ minHeight: 44 }} />
                        </Tabs>
                    </TabBar>

                    {tab === 0 && <LlmUsageMeter summary={llmUsageSummary} />}

                    {tab === 0 && (
                        <>
                            <MessagesArea>
                                {error && (
                                    <Alert
                                        severity={
                                            streamErrorCode === "ai_usage_limit"
                                                ? "warning"
                                                : "error"
                                        }
                                        onClose={() => {
                                            setError(null);
                                            setStreamErrorCode(null);
                                            setStreamErrorDimension(null);
                                        }}
                                    >
                                        {streamErrorCode === "ai_usage_limit" && (
                                            <AlertTitle>Monthly AI budget</AlertTitle>
                                        )}
                                        {streamErrorCode === "ai_usage_limit" &&
                                            streamErrorDimension && (
                                                <Typography
                                                    variant="caption"
                                                    display="block"
                                                    color="text.secondary"
                                                    sx={{ mb: 0.5 }}
                                                >
                                                    Limit reached on:{" "}
                                                    {streamErrorDimension === "tokens"
                                                        ? "tokens"
                                                        : "estimated cost (USD)"}
                                                </Typography>
                                            )}
                                        {error}
                                    </Alert>
                                )}
                                {usageDigestNotice && (
                                    <Alert
                                        severity="warning"
                                        variant="outlined"
                                        onClose={() => setUsageDigestNotice(null)}
                                    >
                                        {usageDigestNotice}
                                    </Alert>
                                )}

                                {messages.length === 0 && !streaming && (
                                    <EmptyState>
                                        <AutoAwesomeIcon sx={{ fontSize: 40, opacity: 0.4 }} />
                                        <Typography variant="h6">
                                            Ask anything about your data
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            textAlign="center"
                                            sx={{ maxWidth: 400 }}
                                        >
                                            {`Try: "Show me PR cycle time by team last month" or "Which developers committed the most this week?"`}
                                        </Typography>
                                    </EmptyState>
                                )}

                                {messages.map((msg) => {
                                    if (msg.role === "user") {
                                        return (
                                            <MessageBubble key={msg.id} $align="user">
                                                <UserBubble>
                                                    <Typography variant="body2">
                                                        {msg.textContent}
                                                    </Typography>
                                                </UserBubble>
                                            </MessageBubble>
                                        );
                                    }

                                    const isSaved = reports.some((r) => r.messageId === msg.id);
                                    const isCompleted = !msg.id.startsWith("temp-");

                                    return (
                                        <MessageBubble key={msg.id} $align="assistant">
                                            <AssistantBubble>
                                                {msg.textContent && (
                                                    <Box
                                                        sx={{
                                                            mb: msg.structuredOutput ? 2 : 0,
                                                            fontSize: "0.875rem",
                                                            lineHeight: 1.6,
                                                            "& p": { margin: "0 0 8px 0" },
                                                            "& p:last-child": { margin: 0 },
                                                            "& ul, & ol": {
                                                                paddingLeft: "1.5em",
                                                                margin: "0 0 8px 0",
                                                            },
                                                            "& li": { marginBottom: "2px" },
                                                            "& strong": { fontWeight: 600 },
                                                            "& code": {
                                                                fontFamily: "monospace",
                                                                fontSize: "0.8em",
                                                                backgroundColor: "action.hover",
                                                                borderRadius: "3px",
                                                                padding: "1px 4px",
                                                            },
                                                            "& table": {
                                                                borderCollapse: "collapse",
                                                                width: "100%",
                                                                fontSize: "0.85em",
                                                                mb: 1,
                                                            },
                                                            "& th, & td": {
                                                                border: "1px solid",
                                                                borderColor: "divider",
                                                                padding: "6px 12px",
                                                                textAlign: "left",
                                                            },
                                                            "& th": {
                                                                backgroundColor: "action.hover",
                                                                fontWeight: 600,
                                                            },
                                                        }}
                                                    >
                                                        <Suspense fallback={msg.textContent}>
                                                            <MarkdownRenderer>
                                                                {msg.textContent}
                                                            </MarkdownRenderer>
                                                        </Suspense>
                                                    </Box>
                                                )}
                                                {!msg.structuredOutput && !msg.textContent && (
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        fontStyle="italic"
                                                    >
                                                        No response generated. Try rephrasing your
                                                        question.
                                                    </Typography>
                                                )}
                                                {msg.structuredOutput && (
                                                    <ReportOutput output={msg.structuredOutput} />
                                                )}
                                                {isCompleted && (
                                                    <Box
                                                        display="flex"
                                                        justifyContent="flex-end"
                                                        alignItems="center"
                                                        mt={1}
                                                        gap={0.5}
                                                    >
                                                        {msg.structuredOutput && (
                                                            <Tooltip
                                                                title={
                                                                    isSaved
                                                                        ? "Saved — view in Insights tab"
                                                                        : "Save as report"
                                                                }
                                                            >
                                                                <IconButton
                                                                    size="small"
                                                                    color={
                                                                        isSaved
                                                                            ? "primary"
                                                                            : "default"
                                                                    }
                                                                    onClick={() => {
                                                                        if (isSaved) {
                                                                            setTab(1);
                                                                            return;
                                                                        }
                                                                        setMessageToSave(msg);
                                                                        setSaveDialogOpen(true);
                                                                    }}
                                                                >
                                                                    {isSaved ? (
                                                                        <BookmarkIcon fontSize="small" />
                                                                    ) : (
                                                                        <BookmarkBorderIcon fontSize="small" />
                                                                    )}
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                        <FeedbackButtons
                                                            targetType="message"
                                                            targetId={msg.id}
                                                        />
                                                    </Box>
                                                )}
                                            </AssistantBubble>
                                        </MessageBubble>
                                    );
                                })}

                                {streaming && streamingDraft && (
                                    <MessageBubble $align="assistant">
                                        <AssistantBubble>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ display: "block", mb: 1 }}
                                            >
                                                Generating…
                                            </Typography>
                                            <Box
                                                sx={{
                                                    fontSize: "0.875rem",
                                                    lineHeight: 1.6,
                                                    "& p": { margin: "0 0 8px 0" },
                                                    "& p:last-child": { margin: 0 },
                                                    "& ul, & ol": {
                                                        paddingLeft: "1.5em",
                                                        margin: "0 0 8px 0",
                                                    },
                                                    "& li": { marginBottom: "2px" },
                                                    "& strong": { fontWeight: 600 },
                                                    "& code": {
                                                        fontFamily: "monospace",
                                                        fontSize: "0.8em",
                                                        backgroundColor: "action.hover",
                                                        borderRadius: "3px",
                                                        padding: "1px 4px",
                                                    },
                                                }}
                                            >
                                                <Suspense fallback={streamingDraft}>
                                                    <MarkdownRenderer>
                                                        {streamingDraft}
                                                    </MarkdownRenderer>
                                                </Suspense>
                                            </Box>
                                        </AssistantBubble>
                                    </MessageBubble>
                                )}

                                <StreamStatus status={streamStatus} />
                                <div ref={messagesEndRef} />
                            </MessagesArea>

                            <InputArea>
                                <TextField
                                    fullWidth
                                    multiline
                                    maxRows={5}
                                    placeholder="Ask a question about your engineering data…"
                                    value={inputText}
                                    onChange={(e) => setInputText(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" && !e.shiftKey) {
                                            e.preventDefault();
                                            handleSend();
                                        }
                                    }}
                                    disabled={streaming}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                {streaming ? (
                                                    <IconButton
                                                        onClick={() => abortRef.current?.abort()}
                                                        color="error"
                                                        size="small"
                                                    >
                                                        <StopIcon />
                                                    </IconButton>
                                                ) : (
                                                    <IconButton
                                                        onClick={handleSend}
                                                        disabled={!inputText.trim()}
                                                        color="primary"
                                                    >
                                                        <SendIcon />
                                                    </IconButton>
                                                )}
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ mt: 0.5, display: "block" }}
                                >
                                    Press Enter to send · Shift+Enter for new line
                                </Typography>
                            </InputArea>
                        </>
                    )}

                    {tab === 1 && (
                        <Box
                            sx={{
                                flex: 1,
                                overflowY: "auto",
                                overflowX: "hidden",
                                maxWidth: "65vw",
                            }}
                        >
                            <SavedReports reports={reports} onReportsChange={setReports} />
                        </Box>
                    )}

                    {tab === 2 && (
                        <Box sx={{ flex: 1, overflowY: "auto", p: 3, maxWidth: "65vw" }}>
                            <Typography variant="h6" gutterBottom>
                                Usage this month
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Token consumption per user for the current calendar month (UTC).
                            </Typography>
                            {historyLoading && <CircularProgress size={24} />}
                            {!historyLoading && usageHistory.length === 0 && (
                                <Typography variant="body2" color="text.secondary">
                                    No usage recorded yet this month.
                                </Typography>
                            )}
                            {!historyLoading && usageHistory.length > 0 && (
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>User</TableCell>
                                                <TableCell align="right">Sessions</TableCell>
                                                <TableCell align="right">Tokens</TableCell>
                                                <TableCell align="right">Est. cost (USD)</TableCell>
                                                <TableCell align="right">Last used</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {usageHistory.map((row) => (
                                                <TableRow key={row.userEmail} hover>
                                                    <TableCell>{row.userEmail}</TableCell>
                                                    <TableCell align="right">
                                                        {row.sessions}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {row.totalTokens.toLocaleString()}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        ${row.estimatedCostUsd.toFixed(4)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {row.lastUsedAt
                                                            ? new Date(
                                                                  row.lastUsedAt
                                                              ).toLocaleDateString(undefined, {
                                                                  month: "short",
                                                                  day: "numeric",
                                                                  hour: "2-digit",
                                                                  minute: "2-digit",
                                                              })
                                                            : "—"}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>
                    )}
                </MainArea>
            </PageContainer>

            <SaveReportDialog
                open={saveDialogOpen}
                onClose={() => {
                    setSaveDialogOpen(false);
                    setMessageToSave(null);
                }}
                onSave={handleSaveReport}
            />
        </>
    );
};

export default AiReports;
